import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface BarcodeScannerProps {
  onScan: (barcode: string) => void;
  onClose: () => void;
  placeholder?: string;
  /** p. ej. z-[110] cuando el escáner se abre encima de otro modal */
  overlayClassName?: string;
}

function playBeep() {
  try {
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 800;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.2, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.1);
  } catch (_) {}
}

export function BarcodeScanner({ onScan, onClose, placeholder, overlayClassName }: BarcodeScannerProps) {
  const [input, setInput] = useState('');
  const bufferRef = useRef({ value: '', lastKeyAt: 0 });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        const { value } = bufferRef.current;
        if (value.length > 2) {
          e.preventDefault();
          playBeep();
          onScan(value.trim());
          bufferRef.current = { value: '', lastKeyAt: 0 };
          setInput('');
        }
        return;
      }
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const now = Date.now();
        if (now - bufferRef.current.lastKeyAt > 120) {
          bufferRef.current.value = '';
        }
        bufferRef.current.value += e.key;
        bufferRef.current.lastKeyAt = now;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onScan]);

  const handleManualSubmit = () => {
    const code = input.trim();
    if (code) {
      playBeep();
      onScan(code);
      setInput('');
    }
  };

  return (
    <div
      className={`fixed inset-0 flex items-center justify-center bg-black/50 p-4 ${overlayClassName ?? 'z-50'}`}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Escanear código
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label="Cerrar"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-6 text-center mb-4">
          <div className="text-5xl mb-2">📷</div>
          <p className="text-base font-semibold text-gray-900 mb-1">
            Listo para escanear
          </p>
          <p className="text-sm text-gray-600">
            Apunte el escáner al código de barras o ingrese abajo
          </p>
        </div>

        <div className="space-y-3">
          <p className="text-sm text-gray-600 text-center">
            O ingrese manualmente:
          </p>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleManualSubmit();
              }
            }}
            placeholder={placeholder || 'Código de barras o SKU'}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            autoFocus
          />
          <button
            type="button"
            onClick={handleManualSubmit}
            disabled={!input.trim()}
            className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Buscar
          </button>
        </div>
      </div>
    </div>
  );
}
